import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";

// Direct imports (avoid Suspense/AnimatePresence conflicts)
import AdminLogin from "@/AdminLogin";
import AdminPanel from "@/AdminPanel";
import UserLogin from "@/UserLogin";
import UserDashboard from "@/UserDashboard";
import MessageModal from "@/components/public/MessageModal";
import MaintenancePage from "@/components/public/MaintenancePage";
import NotFoundPage from "@/components/public/NotFoundPage";

// Eager-loaded (critical path for public page)
import useEmailManager from "@/hooks/useEmailManager";
import { I18nProvider, useI18n } from "@/lib/i18n";
import Header from "@/components/public/Header";
import EmailGenerator from "@/components/public/EmailGenerator";
import TimerWidget from "@/components/public/TimerWidget";
import InboxSection from "@/components/public/InboxSection";
import AddressHistory from "@/components/public/AddressHistory";
import SplashScreen from "@/components/public/SplashScreen";
import InstallPrompt from "@/components/public/InstallPrompt";
import PullToRefresh from "@/components/public/PullToRefresh";
import UpdateNotification from "@/components/public/UpdateNotification";
import OfflineIndicator from "@/components/public/OfflineIndicator";

function App() {
  const [splashDone, setSplashDone] = useState(false);
  // Possible views: "public", "admin-login", "admin-panel", "user-login", "user-dashboard", "404"
  const [currentView, setCurrentView] = useState("public");
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  useEffect(() => {
    const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
    const checkMaintenance = () => {
      axios.get(`${BACKEND_URL}/api/settings`).then(res => {
        setMaintenanceMode(res.data.maintenance_mode || false);
      }).catch(() => {});
    };
    checkMaintenance();
    const interval = setInterval(checkMaintenance, 60000);
    return () => clearInterval(interval);
  }, []);

  const isAdminLoggedIn = useCallback(() => {
    const token = localStorage.getItem("admin_token");
    const expires = localStorage.getItem("admin_expires");
    return token && expires && new Date(expires) > new Date();
  }, []);

  const isUserLoggedIn = useCallback(() => {
    const token = localStorage.getItem("user_token");
    const expires = localStorage.getItem("user_expires");
    return token && expires && new Date(expires) > new Date();
  }, []);

  const handleSplashComplete = useCallback(() => setSplashDone(true), []);

  const resolveView = useCallback(() => {
    const rawHash = window.location.hash;
    // Normalize: remove trailing slashes, query strings, lowercase for matching
    const hash = rawHash.split("?")[0].replace(/\/+$/, "");

    // Admin login: #/shadowpanel/login (check before #/shadowpanel)
    if (hash === "#/shadowpanel/login" || hash === "#shadowpanel/login") {
      if (isAdminLoggedIn()) {
        window.location.hash = "#/shadowpanel";
      } else {
        setCurrentView("admin-login");
      }
      return;
    }

    // Admin dashboard: #/shadowpanel
    if (hash === "#/shadowpanel" || hash === "#shadowpanel") {
      if (isAdminLoggedIn()) {
        localStorage.setItem("admin_mode", "true");
        setCurrentView("admin-panel");
      } else {
        window.location.hash = "#/shadowpanel/login";
      }
      return;
    }

    // User dashboard: #/dashboard
    if (hash === "#/dashboard" || hash === "#dashboard") {
      if (isUserLoggedIn()) {
        setCurrentView("user-dashboard");
      } else {
        window.location.hash = "#/login";
      }
      return;
    }

    // User login: #/login
    if (hash === "#/login" || hash === "#login") {
      if (isUserLoggedIn()) {
        window.location.hash = "#/dashboard";
      } else {
        localStorage.removeItem("admin_mode");
        setCurrentView("user-login");
      }
      return;
    }

    // Home
    if (!hash || hash === "#" || hash === "#/") {
      localStorage.removeItem("admin_mode");
      setCurrentView("public");
      return;
    }

    // Unknown route → redirect to home instead of showing 404
    localStorage.removeItem("admin_mode");
    window.location.hash = "";
  }, [isAdminLoggedIn, isUserLoggedIn]);

  useEffect(() => {
    window.addEventListener("hashchange", resolveView);
    resolveView();
    return () => window.removeEventListener("hashchange", resolveView);
  }, [resolveView]);

  useEffect(() => {
    const titles = {
      "user-login": "ShadowMail - Connexion",
      "user-dashboard": "ShadowMail - Dashboard",
      "admin-login": "ShadowMail - Admin",
      "admin-panel": "ShadowMail - Administration",
      "404": "ShadowMail - Page introuvable",
    };
    if (titles[currentView]) {
      document.title = titles[currentView];
    }
  }, [currentView]);

  const handleAdminLogin = () => {
    localStorage.setItem("admin_mode", "true");
    setCurrentView("admin-panel");
    window.location.hash = "#/shadowpanel";
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_expires");
    localStorage.removeItem("admin_mode");
    setCurrentView("admin-login");
    window.location.hash = "#/shadowpanel/login";
  };

  const handleUserLogin = () => {
    setCurrentView("user-dashboard");
    window.location.hash = "#/dashboard";
  };

  const handleUserLogout = () => {
    localStorage.removeItem("user_token");
    localStorage.removeItem("user_expires");
    localStorage.removeItem("user_username");
    setCurrentView("user-login");
    window.location.hash = "#/login";
  };

  const handleSwitchToPublic = (emailData) => {
    if (emailData) {
      localStorage.setItem('shadowmail_email_id', emailData.id);
      localStorage.setItem('shadowmail_email_data', JSON.stringify(emailData));
    } else {
      localStorage.removeItem('shadowmail_email_id');
      localStorage.removeItem('shadowmail_email_data');
    }
    localStorage.removeItem("admin_mode");
    window.location.hash = "";
  };

  const handleBackToAdmin = () => {
    localStorage.setItem("admin_mode", "true");
    window.location.hash = "#/shadowpanel";
  };

  const handleGoHome = () => {
    window.location.hash = "";
  };

  const toasterConfig = (
    <Toaster
      position="bottom-center"
      theme="dark"
      duration={2000}
      closeButton
      toastOptions={{
        style: {
          background: '#0a0a0a',
          border: '1px solid #27272a',
          color: '#ffffff',
        },
      }}
    />
  );

  return (
    <I18nProvider>
      <SplashScreen onComplete={handleSplashComplete} />
      {splashDone && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
            {currentView === "404" ? (
              <>
                {toasterConfig}
                <NotFoundPage onGoHome={handleGoHome} />
              </>
            ) : currentView === "admin-login" ? (
              <>
                {toasterConfig}
                <AdminLogin onLogin={handleAdminLogin} />
              </>
            ) : currentView === "admin-panel" ? (
              <>
                {toasterConfig}
                <AdminPanel onLogout={handleAdminLogout} onSwitchToPublic={handleSwitchToPublic} />
              </>
            ) : currentView === "user-login" ? (
              maintenanceMode ? (
                <MaintenancePage />
              ) : (
              <>
                {toasterConfig}
                <UserLogin onLogin={handleUserLogin} />
              </>
              )
            ) : currentView === "user-dashboard" ? (
              maintenanceMode ? (
                <MaintenancePage />
              ) : (
              <>
                {toasterConfig}
                <UserDashboard onLogout={handleUserLogout} />
              </>
              )
            ) : (
              <PublicApp isAdminLoggedIn={isAdminLoggedIn()} onBackToAdmin={handleBackToAdmin} isUserLoggedIn={isUserLoggedIn()} onGoToDashboard={() => { window.location.hash = "#/dashboard"; }} />
            )}
        </motion.div>
      )}
    </I18nProvider>
  );
}

function PublicApp({ isAdminLoggedIn, onBackToAdmin, isUserLoggedIn, onGoToDashboard }) {
  const userUsername = localStorage.getItem("user_username") || "";
  const em = useEmailManager();
  const { t } = useI18n();

  // Set maintenance title when in maintenance mode
  useEffect(() => {
    if (em.maintenanceMode) {
      document.title = "ShadowMail - Maintenance";
    }
  }, [em.maintenanceMode]);

  // Theme state
  const [theme, setTheme] = useState(() => localStorage.getItem('shadowmail_theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('shadowmail_theme', theme);
    // Update meta theme-color
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#050505' : '#f5f5f5');
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  // Handle manifest shortcut ?action=generate
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("action") === "generate" && !em.isLoading) {
      em.generateEmail();
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
  }, [em.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-screen flex flex-col grid-background animate-page-in overflow-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <Toaster
        position="bottom-center"
        theme={theme}
        duration={2000}
        closeButton
        toastOptions={{
          style: theme === 'dark' ? {
            background: '#0a0a0a',
            border: '1px solid #27272a',
            color: '#ffffff',
          } : {
            background: '#ffffff',
            border: '1px solid #e4e4e9',
            color: '#1a1a2e',
          },
        }}
      />

      {em.maintenanceMode ? (
        <MaintenancePage />
      ) : (
        <>
          <Header
            siteName={em.siteName}
            isAdminLoggedIn={isAdminLoggedIn}
            onBackToAdmin={onBackToAdmin}
            isUserLoggedIn={isUserLoggedIn}
            userUsername={userUsername}
            onGoToDashboard={onGoToDashboard}
            theme={theme}
            toggleTheme={toggleTheme}
          />

          <main className="flex-1 max-w-6xl mx-auto px-3 sm:px-4 py-2 sm:py-6 overflow-auto w-full">
            <PullToRefresh onRefresh={em.refreshInbox} disabled={!em.email}>
              <div className="grid grid-cols-12 gap-3 sm:gap-6">
                <EmailGenerator
                  email={em.email}
                  isLoading={em.isLoading}
                  selectedLifetime={em.selectedLifetime}
                  setSelectedLifetime={em.setSelectedLifetime}
                  lifetimeOptions={em.lifetimeOptions}
                  allowPermanent={em.allowPermanent}
                  allowCustomAddress={em.allowCustomAddress}
                  customAddress={em.customAddress}
                  setCustomAddress={em.setCustomAddress}
                  customAddressError={em.customAddressError}
                  setCustomAddressError={em.setCustomAddressError}
                  showCustomInput={em.showCustomInput}
                  setShowCustomInput={em.setShowCustomInput}
                  generateEmail={em.generateEmail}
                  copyToClipboard={em.copyToClipboard}
                  deleteEmail={em.deleteEmail}
                  welcomeText={em.welcomeText}
                  domains={em.domains}
                  selectedDomain={em.selectedDomain}
                  setSelectedDomain={em.setSelectedDomain}
                />

                <TimerWidget
                  email={em.email}
                  isPermanent={em.isPermanent}
                  isCritical={em.isCritical}
                  remainingTime={em.remainingTime}
                  currentLifetime={em.currentLifetime}
                  lifetimeOptions={em.lifetimeOptions}
                  progressValue={em.progressValue}
                  formatTime={em.formatTime}
                />

                <InboxSection
                  email={em.email}
                  messages={em.messages}
                  selectedMessages={em.selectedMessages}
                  isDeletingMessages={em.isDeletingMessages}
                  isRefreshingInbox={em.isRefreshingInbox}
                  refreshInbox={em.refreshInbox}
                  selectAllMessages={em.selectAllMessages}
                  toggleMessageSelection={em.toggleMessageSelection}
                  deleteSelectedMessages={em.deleteSelectedMessages}
                  markSelectedMessages={em.markSelectedMessages}
                  fetchMessage={em.fetchMessage}
                  formatDate={em.formatDate}
                />

                <AddressHistory
                  addressHistory={em.addressHistory}
                  clearAddressHistory={em.clearAddressHistory}
                />
              </div>
            </PullToRefresh>
          </main>

          {/* Footer */}
          <footer className="relative mt-auto overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-cyan-500/[0.03] to-transparent pointer-events-none" />
            <div className="border-t border-zinc-800/50 relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
              <div className="max-w-6xl mx-auto px-4 py-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center overflow-hidden">
                        <img src="/icons/icon-96.png" alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = '<span class="text-cyan-400 text-[9px] font-bold font-mono">&lt;/&gt;</span>'; }} />
                      </div>
                      <span className="text-xs font-mono font-semibold">
                        <span className="text-zinc-600">&lt;</span>
                        <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">{em.siteName || 'ShadowMail'}</span>
                        <span className="text-zinc-600"> /&gt;</span>
                      </span>
                    </div>
                    <span className="text-zinc-800">•</span>
                    <span className="text-zinc-600 text-[10px] font-mono">&copy; {new Date().getFullYear()} {t.allRightsReserved}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/60 animate-pulse" />
                    <p className="text-zinc-500 text-[10px] sm:text-[11px] font-mono text-center sm:text-right">
                      {em.footerText || t.footerDefault}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </footer>

          <MessageModal
              isOpen={em.isModalOpen}
              onOpenChange={em.setIsModalOpen}
              message={em.selectedMessage}
              emailId={em.email?.id}
              replyEnabled={em.email?.reply_enabled}
              messages={em.messages}
              fetchMessage={em.fetchMessage}
              deleteMessage={em.deleteMessage}
            />

          <InstallPrompt />
          <UpdateNotification />
          <OfflineIndicator />
        </>
      )}
    </div>
  );
}

export default App;
